import { create } from 'zustand';
import {
  WORKSPACE_FEATURES,
  type AuthResult,
  type Organization,
  type PendingRegistration,
  type PublicUser,
  type Role,
  type WorkspaceFeature,
  type WorkspaceIndustry,
} from '@itsappy/shared';
import { api, tokenStore } from '@/lib/api';

interface AuthState {
  user: PublicUser | null;
  organizations: Organization[];
  orgId: string | null;
  role: Role | null;
  features: WorkspaceFeature[];
  /** Products the workspace has switched off for everyone, owners included. */
  disabledFeatures: WorkspaceFeature[];
  /** Can review workspace requests. Sits outside any organization's roles. */
  isPlatformAdmin: boolean;
  loading: boolean;

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Resolves with a `PendingRegistration` when the account was created but has
   * nothing to sign into yet — a new workspace waiting on a platform admin, or
   * a join request waiting on that workspace's owner. Resolves with `null`
   * when the caller is signed in.
   */
  register: (input: {
    name: string;
    email: string;
    password: string;
    /** E.164, or `''` when the number box was left empty. */
    phone?: string;
    orgName?: string;
    /** What the requester says the workspace does; the reviewer confirms it. */
    orgIndustry?: WorkspaceIndustry;
    /** The code that says which workspace to join. */
    joinCode?: string;
  }) => Promise<PendingRegistration | null>;
  logout: () => Promise<void>;
  switchOrg: (orgId: string) => void;
  setUser: (user: PublicUser) => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  organizations: [],
  orgId: tokenStore.orgId,
  role: null,
  features: [...WORKSPACE_FEATURES],
  disabledFeatures: [],
  isPlatformAdmin: false,
  loading: true,

  async bootstrap() {
    if (!tokenStore.access) {
      set({ loading: false });
      return;
    }
    try {
      const me = await api.get<{
        user: PublicUser;
        isPlatformAdmin: boolean;
        organizations: Organization[];
        roles: Record<string, Role>;
        features: Record<string, WorkspaceFeature[]>;
        disabledFeatures?: Record<string, WorkspaceFeature[]>;
        mediaToken?: string;
      }>('/auth/me');

      // A reload with a still-valid access token never touches /refresh, so
      // this is where a returning tab picks up a fresh pass for /uploads.
      if (me.mediaToken) tokenStore.setMedia(me.mediaToken);

      const orgId =
        tokenStore.orgId && me.organizations.some((o) => o.id === tokenStore.orgId)
          ? tokenStore.orgId
          : me.organizations[0]?.id ?? null;
      if (orgId) tokenStore.setOrg(orgId);

      set({
        user: me.user,
        organizations: me.organizations,
        orgId,
        role: orgId ? me.roles[orgId] ?? null : null,
        features: orgId ? me.features[orgId] ?? [...WORKSPACE_FEATURES] : [],
        disabledFeatures: orgId ? me.disabledFeatures?.[orgId] ?? [] : [],
        isPlatformAdmin: me.isPlatformAdmin === true,
        loading: false,
      });
    } catch {
      tokenStore.clear();
      set({
        user: null,
        organizations: [],
        orgId: null,
        role: null,
        features: [],
        disabledFeatures: [],
        isPlatformAdmin: false,
        loading: false,
      });
    }
  },

  async login(email, password) {
    // A 401 here means the credentials are wrong, not that an existing session
    // expired. Let the form receive and display the server's real message.
    const result = await api.post<AuthResult>(
      '/auth/login',
      { email, password },
      { skipAuthRetry: true },
    );
    tokenStore.set(result);
    const orgId = result.organizations[0]?.id ?? null;
    if (orgId) tokenStore.setOrg(orgId);
    set({ user: result.user, organizations: result.organizations, orgId, loading: false });
    await get().bootstrap();
  },

  async register(input) {
    const result = await api.post<AuthResult | PendingRegistration>('/auth/register', input);
    // Creating a workspace, and joining someone else's, both end in a queue
    // rather than a session — there is no token to store and nowhere to go.
    if ('pending' in result) return result;

    tokenStore.set(result);
    const orgId = result.organizations[0]?.id ?? null;
    if (orgId) tokenStore.setOrg(orgId);
    set({ user: result.user, organizations: result.organizations, orgId, loading: false });
    await get().bootstrap();
    return null;
  },

  async logout() {
    const refreshToken = tokenStore.refresh;
    tokenStore.clear();
    set({
      user: null,
      organizations: [],
      orgId: null,
      role: null,
      features: [],
      disabledFeatures: [],
      isPlatformAdmin: false,
    });
    if (refreshToken) await api.post('/auth/logout', { refreshToken }).catch(() => undefined);
  },

  switchOrg(orgId) {
    tokenStore.setOrg(orgId);
    set({ orgId });
    // A full reload is the simplest way to drop every org-scoped cache.
    window.location.reload();
  },

  setUser(user) {
    set({ user });
  },
}));
